import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, LinearTransition, useReducedMotion } from 'react-native-reanimated';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { KnockoutTree } from '@/components/predictor/KnockoutTree';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useMyFantasyTeams } from '@/features/fantasy/hooks';
import { useCompetitions } from '@/features/leagues/hooks';
import { useStandings } from '@/features/matches/hooks';
import {
  allocateThirds,
  buildR32,
  GROUP_KEYS,
  resolveTies,
  ROUND_KEYS,
  ROUND_MATCHES,
  type GroupKey,
  type Qualifiers,
  type RoundKey,
} from '@/features/predictor/bracket';
import {
  useMyBracket,
  useMyBracketStats,
  useSaveBracket,
} from '@/features/predictor/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

const WC_PROVIDER_ID = 9900001;

interface TeamRef {
  id: string;
  name: string;
  short_name: string | null;
  logo_url: string | null;
}

type Step = 'groups' | 'thirds' | 'knockout';

function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Qualification tone from a group rank: top 2 advance, 3rd is a best-third
 *  maybe, 4th is out. Drives the row colour in the groups step. */
type QualTone = 'qualify' | 'third' | 'out';
function toneForRank(rank: number): QualTone {
  if (rank <= 2) return 'qualify';
  if (rank === 3) return 'third';
  return 'out';
}

/**
 * A flag + name chip. In the groups step (`qualify`) the row is tinted by its
 * predicted finish - green for the top two that advance, gold for the best-third
 * maybe, red for the eliminated fourth - so the table reads at a glance like the
 * reference simulator. Elsewhere it shows a volt rank badge / pick check.
 */
function TeamRow({
  team,
  rank,
  picked,
  dim,
  qualify,
  onPress,
}: {
  team: TeamRef | undefined;
  rank?: number;
  picked?: boolean;
  dim?: boolean;
  /** Colour the row by qualification (groups step). */
  qualify?: boolean;
  onPress?: () => void;
}) {
  const palette = useThemePalette();
  const { arabicNumerals, locale } = useLocale();
  const tone: QualTone | null = qualify && rank ? toneForRank(rank) : null;

  const container = picked
    ? 'border-volt bg-volt/10'
    : tone === 'qualify'
      ? 'border-win/50 bg-win/10'
      : tone === 'third'
        ? 'border-captain/50 bg-captain/10'
        : tone === 'out'
          ? 'border-loss/45 bg-loss/10'
          : rank
            ? 'border-volt-line bg-surface'
            : 'border-line bg-surface';

  const badgeColor =
    tone === 'qualify'
      ? palette.win
      : tone === 'third'
        ? palette.captain
        : tone === 'out'
          ? palette.loss
          : palette.volt;
  const badgeText = tone ? '#06070E' : palette.onVolt;

  const trailingIcon =
    tone === 'qualify'
      ? ('arrow-up' as const)
      : tone === 'third'
        ? ('star' as const)
        : tone === 'out'
          ? ('arrow-down' as const)
          : null;
  const trailingColor =
    tone === 'qualify' ? palette.win : tone === 'third' ? palette.captain : palette.loss;

  const body = (
    <View
      className={`flex-row items-center gap-2.5 rounded-xl border px-3 py-2.5 ${container} ${
        dim ? 'opacity-40' : ''
      }`}
    >
      {rank ? (
        <View
          className="h-5 w-5 items-center justify-center rounded-full"
          style={{ backgroundColor: badgeColor }}
        >
          <AppText variant="micro" weight="bold" tabular style={{ color: badgeText }}>
            {localizeDigits(String(rank), arabicNumerals)}
          </AppText>
        </View>
      ) : null}
      <TeamCrest uri={team?.logo_url} name={localizeTeamName(team, locale)} size="sm" />
      <AppText variant="caption" weight="semibold" className="flex-1 text-ink" numberOfLines={1}>
        {localizeTeamName(team, locale, true)}
      </AppText>
      {picked ? (
        <Ionicons name="checkmark-circle" size={18} color={palette.voltBright} />
      ) : trailingIcon ? (
        <Ionicons name={trailingIcon} size={15} color={trailingColor} />
      ) : null}
    </View>
  );
  if (!onPress) return body;
  return (
    <Pressable accessibilityRole="button" onPress={onPress} className="active:opacity-80">
      {body}
    </Pressable>
  );
}

/** A circular, centre-cropped flag - the look of the reference's thirds grid. */
function CircleFlag({ uri, name, size }: { uri: string | null; name: string; size: number }) {
  return (
    <View
      className="items-center justify-center overflow-hidden rounded-full border border-line bg-surface-2"
      style={{ width: size, height: size }}
    >
      {uri ? (
        <Image
          source={{ uri }}
          style={{ width: size, height: size }}
          contentFit="cover"
          cachePolicy="memory-disk"
        />
      ) : (
        <AppText variant="caption" weight="bold" className="text-soft">
          {name.slice(0, 2).toUpperCase()}
        </AppText>
      )}
    </View>
  );
}

/** Coloured-dot + label legend chip (qualify / 3rd / out). */
function Legend({ color, label }: { color: string; label: string }) {
  return (
    <View className="flex-row items-center gap-1.5">
      <View className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <AppText variant="micro" className="text-muted">
        {label}
      </AppText>
    </View>
  );
}

/** A best-third pick card: group label, big circular flag, name, cyan ring when picked. */
function ThirdCard({
  team,
  groupLabel,
  picked,
  dim,
  onPress,
}: {
  team: TeamRef | undefined;
  groupLabel: string;
  picked: boolean;
  dim: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: picked }}
      onPress={onPress}
      style={{ width: '48%' }}
      className={`mb-3 items-center gap-2 rounded-2xl border px-2 py-4 ${
        picked ? 'border-volt bg-volt/10' : 'border-line bg-surface'
      } ${dim ? 'opacity-40' : ''}`}
    >
      <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
        {groupLabel}
      </AppText>
      <View>
        <CircleFlag uri={team?.logo_url ?? null} name={localizeTeamName(team, locale)} size={56} />
        {picked ? (
          <View className="absolute -end-1 -top-1 h-5 w-5 items-center justify-center rounded-full bg-volt">
            <Ionicons name="checkmark" size={13} color={palette.onVolt} />
          </View>
        ) : null}
      </View>
      <AppText variant="caption" weight="semibold" className="text-center text-ink" numberOfLines={1}>
        {localizeTeamName(team, locale, true)}
      </AppText>
    </Pressable>
  );
}

/**
 * The flagship headline of the predictor: the user's CHAMPION pick surfaced as a
 * prominent crest + name card, or an "اختر البطل / Choose your champion" CTA when
 * unset. Champion is payload slot 104; this only reads it.
 */
function ChampionHeadline({
  champion,
  onPress,
}: {
  champion: TeamRef | undefined;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const picked = Boolean(champion);
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className={`mx-4 mb-3 flex-row items-center gap-3 overflow-hidden rounded-card border px-4 py-3.5 active:opacity-90 ${
        picked ? 'border-captain/50 bg-captain/10' : 'border-dashed border-volt-line bg-volt-glass'
      }`}
    >
      <View className="h-14 w-14 items-center justify-center rounded-2xl bg-volt-glass-strong">
        {champion ? (
          <TeamCrest uri={champion.logo_url} name={localizeTeamName(champion, locale)} size="lg" />
        ) : (
          <Ionicons name="trophy" size={26} color={palette.captain} />
        )}
      </View>
      <View className="flex-1">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
          {t('predictor.championLabel')}
        </AppText>
        {champion ? (
          <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={1}>
            {localizeTeamName(champion, locale, true)}
          </AppText>
        ) : (
          <AppText variant="caption" weight="bold" className="text-volt-bright" numberOfLines={2}>
            {t('predictor.championCta')}
          </AppText>
        )}
      </View>
      <Ionicons name={picked ? 'create-outline' : 'chevron-forward'} size={18} color={palette.muted} />
    </Pressable>
  );
}

/** Tappable "your rank · points" contest card shown after the bracket is saved. */
function RankStatCard({
  rank,
  points,
  onPress,
}: {
  rank: number;
  points: number;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const n = (x: number) => formatNumber(x, numeralOptions);
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className="mx-4 mb-3 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3 active:opacity-90"
    >
      <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass-strong">
        <Ionicons name="podium" size={18} color={palette.voltBright} />
      </View>
      <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
        {t('predictor.rankStat', { rank: `#${n(rank)}`, points: n(points) })}
      </AppText>
      <Ionicons name="chevron-forward" size={18} color={palette.voltBright} />
    </Pressable>
  );
}

/** A non-interactive prize callout: top finishers win coins. */
function PrizeCallout() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <View className="mx-4 mb-3 flex-row items-center gap-2.5 rounded-card border border-captain/40 bg-captain/10 px-4 py-3">
      <Ionicons name="gift" size={18} color={palette.captain} />
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink">
          {t('predictor.prizeTitle')}
        </AppText>
        <AppText variant="micro" className="text-soft">
          {t('predictor.prizeBody')}
        </AppText>
      </View>
    </View>
  );
}

export default function PredictorScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const toast = useToast();
  const reduce = useReducedMotion();
  const howto = useHowToPlay('predictor');

  const competitions = useCompetitions();
  const wc = (competitions.data ?? []).find((c) => c.provider_id === WC_PROVIDER_ID);
  const standings = useStandings(wc?.id, 2026);
  const seasonsQuery = useMyFantasyTeams();
  const wcSeasonId =
    (seasonsQuery.data ?? []).find((team) =>
      (team.fantasy_season?.competition?.name ?? '').toLowerCase().includes('world cup'),
    )?.fantasy_season?.id ?? null;

  const myBracket = useMyBracket();
  const saveBracket = useSaveBracket();
  const bracketStats = useMyBracketStats();

  const [step, setStep] = useState<Step>('groups');
  const [knockoutRound, setKnockoutRound] = useState<RoundKey>('R32');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [order, setOrder] = useState<Record<string, string[]>>({});
  const [thirds, setThirds] = useState<string[]>([]);
  const [picks, setPicks] = useState<Record<number, string | null>>({});
  const [loaded, setLoaded] = useState(false);

  // Group letter → its 4 teams (current standings order), + a team lookup.
  const { byKey, teamById } = useMemo(() => {
    const map = new Map<string, TeamRef[]>();
    const lookup = new Map<string, TeamRef>();
    for (const row of standings.data ?? []) {
      const team = row.team as TeamRef | null;
      if (!row.group_label || !team) continue;
      const key = row.group_label.replace(/group\s*/i, '').trim().toUpperCase();
      const arr = map.get(key) ?? [];
      arr.push(team);
      map.set(key, arr);
      lookup.set(team.id, team);
    }
    return { byKey: map, teamById: lookup };
  }, [standings.data]);

  // Prefill once from a saved bracket - the React-docs "adjust state when data
  // arrives" pattern (guarded render-phase set, not an effect).
  if (!loaded && myBracket.data) {
    const data = myBracket.data;
    if (data.groups) setOrder(data.groups);
    if (data.thirds) setThirds(data.thirds);
    if (data.picks) {
      const numeric: Record<number, string | null> = {};
      for (const [k, v] of Object.entries(data.picks)) numeric[Number(k)] = v;
      setPicks(numeric);
    }
    setLoaded(true);
  }

  const groupOf = (teamId: string): GroupKey | undefined => {
    for (const key of GROUP_KEYS) {
      if (byKey.get(key)?.some((team) => team.id === teamId)) return key;
    }
    return undefined;
  };

  const qualifiers: Qualifiers = useMemo(() => {
    const q: Qualifiers = { winner: {}, runnerUp: {}, third: {} };
    for (const key of GROUP_KEYS) {
      const o = order[key];
      if (o?.[0]) q.winner[key] = o[0];
      if (o?.[1]) q.runnerUp[key] = o[1];
      if (o?.[2]) q.third[key] = o[2];
    }
    return q;
  }, [order]);

  const thirdsPool = useMemo(
    () => GROUP_KEYS.map((k) => qualifiers.third[k]).filter((id): id is string => Boolean(id)),
    [qualifiers],
  );
  const validThirds = useMemo(() => thirds.filter((id) => thirdsPool.includes(id)), [thirds, thirdsPool]);
  // If a group was reordered, a previously-chosen third may no longer finish 3rd.
  // Drop those stale picks so the slot frees up and the picker can't get stuck at
  // a fake "8/8" (guarded render-phase reconcile; self-terminates).
  if (validThirds.length !== thirds.length) {
    setThirds(validThirds);
  }

  const ties = useMemo(() => {
    const thirdBySlot = allocateThirds(validThirds, groupOf);
    const r32 = buildR32(qualifiers, thirdBySlot);
    return resolveTies(r32, picks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualifiers, validThirds, picks, byKey]);

  const winnerOf = (matchId: number): string | null => {
    const tie = ties[matchId];
    const pick = picks[matchId];
    if (pick && tie && (pick === tie.home || pick === tie.away)) return pick;
    return null;
  };
  const championId = winnerOf(104);

  const allGroupsDone = GROUP_KEYS.every((k) => (order[k]?.length ?? 0) === 4);
  const groupsDone = GROUP_KEYS.filter((k) => (order[k]?.length ?? 0) === 4).length;

  const tapGroupTeam = (key: string, teamId: string) =>
    setOrder((prev) => {
      const cur = prev[key] ?? [];
      if (cur.includes(teamId)) return { ...prev, [key]: cur.filter((id) => id !== teamId) };
      if (cur.length >= 4) return prev;
      return { ...prev, [key]: [...cur, teamId] };
    });

  const toggleThird = (teamId: string) =>
    setThirds((prev) => {
      if (prev.includes(teamId)) return prev.filter((id) => id !== teamId);
      if (prev.length >= 8) return prev;
      return [...prev, teamId];
    });

  const pickWinner = (matchId: number, teamId: string) =>
    setPicks((prev) => ({ ...prev, [matchId]: prev[matchId] === teamId ? null : teamId }));

  // --- Randomisers (try your luck per section) ---------------------------
  const randomizeGroups = () => {
    const next: Record<string, string[]> = {};
    for (const key of GROUP_KEYS) {
      next[key] = shuffle((byKey.get(key) ?? []).map((team) => team.id));
    }
    setOrder(next);
  };

  const randomizeThirds = () => setThirds(shuffle(thirdsPool).slice(0, 8));

  const randomizeKnockout = () => {
    const r32 = buildR32(qualifiers, allocateThirds(validThirds, groupOf));
    const next: Record<number, string | null> = {};
    for (const round of ROUND_KEYS) {
      const resolved = resolveTies(r32, next);
      for (const matchId of ROUND_MATCHES[round]) {
        const tie = resolved[matchId];
        const options = [tie?.home, tie?.away].filter((id): id is string => Boolean(id));
        if (options.length === 2) {
          next[matchId] = options[Math.floor(Math.random() * options.length)];
        }
      }
    }
    setPicks(next);
  };

  const randomizeCurrent = () => {
    if (step === 'groups') randomizeGroups();
    else if (step === 'thirds') randomizeThirds();
    else randomizeKnockout();
  };

  const onSave = () => {
    if (!userId) {
      router.push('/auth/sign-in');
      return;
    }
    saveBracket.mutate(
      {
        seasonId: wcSeasonId ?? null,
        payload: {
          groups: order,
          thirds: validThirds,
          picks: Object.fromEntries(Object.entries(picks).map(([k, v]) => [k, v])),
          champion: championId,
        },
      },
      {
        onSuccess: () => toast.show({ message: t('predictor.saved'), tone: 'success' }),
        onError: () => toast.show({ message: t('common.errorBody'), tone: 'error' }),
      },
    );
  };

  if (standings.isPending || competitions.isPending) {
    return (
      <Screen>
        <BackHeader title={t('predictor.title')} right={<HowToPlayButton onPress={howto.open} />} />
        <View className="gap-3 px-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </View>
      </Screen>
    );
  }

  if (byKey.size === 0) {
    return (
      <Screen>
        <BackHeader title={t('predictor.title')} right={<HowToPlayButton onPress={howto.open} />} />
        <EmptyState icon="git-network-outline" title={t('predictor.emptyTitle')} body={t('predictor.emptyBody')} />
      </Screen>
    );
  }

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="predictor" icon="git-network" />
      <BackHeader
        title={t('predictor.title')}
        right={
          <View className="flex-row items-center gap-2">
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('predictor.leaderboard')}
              onPress={() => router.push('/predictor/leaderboard')}
              className="h-9 w-9 items-center justify-center rounded-full bg-surface active:bg-surface-2"
            >
              <Ionicons name="podium-outline" size={18} color={palette.voltBright} />
            </Pressable>
            <HowToPlayButton onPress={howto.open} />
          </View>
        }
      />

      {/* Step indicator */}
      <View className="flex-row gap-2 px-4 pb-3">
        {(['groups', 'thirds', 'knockout'] as Step[]).map((s, i) => {
          const active = s === step;
          const done = (['groups', 'thirds', 'knockout'] as Step[]).indexOf(step) > i;
          return (
            <View
              key={s}
              className={`h-1.5 flex-1 rounded-full ${active ? 'bg-volt' : done ? 'bg-volt/40' : 'bg-line'}`}
            />
          );
        })}
      </View>

      {/* Flagship headline: your champion pick, your contest rank, the prize. */}
      <ChampionHeadline
        champion={championId ? teamById.get(championId) : undefined}
        onPress={() => setStep('knockout')}
      />
      {myBracket.data && bracketStats.data ? (
        <RankStatCard
          rank={bracketStats.data.rank}
          points={bracketStats.data.points}
          onPress={() => router.push('/games/leaderboards-hub?tab=bracket' as never)}
        />
      ) : null}
      <PrizeCallout />

      {step === 'groups' ? (
        <Animated.View entering={reduce ? undefined : FadeIn} style={{ flex: 1 }}>
          <View className="flex-row items-center justify-between px-4 pb-1">
            <AppText variant="heading">{t('predictor.groupsTitle')}</AppText>
            <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
              {groupsDone}/12
            </AppText>
          </View>
          <AppText variant="micro" className="px-4 pb-2 text-muted">
            {t('predictor.groupsHint')}
          </AppText>
          <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 100 }}>
            {GROUP_KEYS.map((key) => {
              const teams = byKey.get(key) ?? [];
              const o = order[key] ?? [];
              return (
                <Card key={key} className="mb-3 gap-2 p-3">
                  <View className="flex-row items-center justify-between">
                    <AppText variant="caption" weight="bold" className="uppercase tracking-widest text-soft">
                      {t('matches.group')} {key}
                    </AppText>
                    {o.length > 0 ? (
                      <Pressable accessibilityRole="button" onPress={() => setOrder((p) => ({ ...p, [key]: [] }))}>
                        <AppText variant="micro" className="text-muted">
                          {t('predictor.reset')}
                        </AppText>
                      </Pressable>
                    ) : null}
                  </View>
                  <View className="gap-2">
                    {/* Ranked teams float to the top in pick order; the flags
                        glide into place as you choose 1st→4th. */}
                    {[
                      ...o
                        .map((id) => teams.find((team) => team.id === id))
                        .filter((team): team is TeamRef => Boolean(team)),
                      ...teams.filter((team) => !o.includes(team.id)),
                    ].map((team) => {
                      const rank = o.indexOf(team.id);
                      return (
                        <Animated.View
                          key={team.id}
                          layout={reduce ? undefined : LinearTransition.springify().damping(18)}
                        >
                          <TeamRow
                            team={team}
                            rank={rank >= 0 ? rank + 1 : undefined}
                            qualify
                            onPress={() => tapGroupTeam(key, team.id)}
                          />
                        </Animated.View>
                      );
                    })}
                  </View>
                  <View className="flex-row items-center justify-center gap-4 border-t border-line pt-2">
                    <Legend color={palette.win} label={t('predictor.legendQualify')} />
                    <Legend color={palette.captain} label={t('predictor.legendThird')} />
                    <Legend color={palette.loss} label={t('predictor.legendOut')} />
                  </View>
                </Card>
              );
            })}
          </ScrollView>
        </Animated.View>
      ) : step === 'thirds' ? (
        <Animated.View entering={reduce ? undefined : FadeIn} style={{ flex: 1 }}>
          <View className="flex-row items-center justify-between px-4 pb-1">
            <AppText variant="heading">{t('predictor.thirdsTitle')}</AppText>
            <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
              {validThirds.length}/8
            </AppText>
          </View>
          <AppText variant="micro" className="px-4 pb-2 text-muted">
            {t('predictor.thirdsHint')}
          </AppText>
          <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 100 }}>
            <View className="flex-row flex-wrap justify-between">
              {thirdsPool.map((teamId) => (
                <ThirdCard
                  key={teamId}
                  team={teamById.get(teamId)}
                  groupLabel={`${t('matches.group')} ${groupOf(teamId) ?? ''}`}
                  picked={thirds.includes(teamId)}
                  dim={thirds.length >= 8 && !thirds.includes(teamId)}
                  onPress={() => toggleThird(teamId)}
                />
              ))}
            </View>
          </ScrollView>
        </Animated.View>
      ) : (
        <Animated.View entering={reduce ? undefined : FadeIn} style={{ flex: 1 }}>
          <AppText variant="micro" className="px-4 pb-2 text-muted">
            {t('predictor.knockoutHint')}
          </AppText>
          <ScrollView contentContainerStyle={{ paddingVertical: 8, paddingBottom: 120 }}>
            <KnockoutTree
              teamById={teamById}
              ties={ties}
              picks={picks}
              championId={championId}
              wcLogo={wc?.logo_url ?? null}
              round={knockoutRound}
              onRoundChange={setKnockoutRound}
              onPick={pickWinner}
            />
          </ScrollView>
        </Animated.View>
      )}

      {/* Footer nav */}
      <View className="flex-row items-center gap-3 border-t border-line px-4 py-3">
        {step !== 'groups' ? (
          <Button
            label={t('common.back')}
            variant="ghost"
            compact
            onPress={() => setStep(step === 'thirds' ? 'groups' : 'thirds')}
          />
        ) : null}
        <Button label={t('predictor.random')} variant="ghost" compact onPress={randomizeCurrent} />
        <View className="flex-1" />
        {step === 'groups' ? (
          <Button label={t('auth.next')} disabled={!allGroupsDone} onPress={() => setStep('thirds')} />
        ) : step === 'thirds' ? (
          <Button label={t('auth.next')} disabled={validThirds.length !== 8} onPress={() => setStep('knockout')} />
        ) : (
          <>
            {knockoutRound !== ROUND_KEYS[ROUND_KEYS.length - 1] ? (
              <Button
                label={t('predictor.nextRound')}
                variant="ghost"
                compact
                onPress={() =>
                  setKnockoutRound(ROUND_KEYS[ROUND_KEYS.indexOf(knockoutRound) + 1])
                }
              />
            ) : null}
            <Button
              label={t('predictor.save')}
              loading={saveBracket.isPending}
              disabled={!championId}
              onPress={() => setConfirmOpen(true)}
            />
          </>
        )}
      </View>

      {/* Final-lock confirmation - the prediction can't be changed after this. */}
      <Modal
        visible={confirmOpen}
        animationType="fade"
        transparent
        onRequestClose={() => setConfirmOpen(false)}
      >
        <Pressable className="flex-1 justify-end bg-black/60" onPress={() => setConfirmOpen(false)}>
          <Pressable
            onPress={(event) => event.stopPropagation()}
            className="gap-3 rounded-t-3xl border border-line bg-surface-2 px-5 pb-8 pt-5"
          >
            <View className="flex-row items-center gap-2">
              <Ionicons name="lock-closed" size={18} color={palette.captain} />
              <AppText variant="heading" weight="bold" className="text-ink">
                {t('predictor.lockTitle')}
              </AppText>
            </View>
            <AppText variant="caption" className="leading-5 text-soft">
              {t('predictor.lockBody')}
            </AppText>
            <View className="mt-1 gap-2">
              <Button
                label={t('predictor.lockConfirm')}
                loading={saveBracket.isPending}
                onPress={() => {
                  setConfirmOpen(false);
                  onSave();
                }}
              />
              <Button
                label={t('common.cancel')}
                variant="ghost"
                compact
                onPress={() => setConfirmOpen(false)}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </Screen>
  );
}
